import {
  GoogleReviewListItem,
  GoogleReviewsAnalyzeResult,
  GoogleReviewsDashboardData,
  GoogleReviewsInsightsResult,
  GoogleReviewsListResponse,
  GoogleReviewsRunHistoryItem,
  GoogleReviewsScrapeActionResult
} from '../types/googleReviews';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

type ApiSuccessResponse<TData> = {
  success: boolean;
  data: TData;
  meta?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  error?: string;
};

type GoogleReviewsRunsFilters = {
  page?: number;
  pageSize?: number;
  runType?: 'scrape' | 'analysis' | 'all';
  status?: string;
  businessName?: string;
  datasetId?: string;
};

type GoogleReviewsReviewFilters = {
  page?: number;
  pageSize?: number;
  datasetId?: string;
  businessName?: string;
  source?: string;
  search?: string;
  minRating?: number;
  maxRating?: number;
  publishedFrom?: string;
  publishedTo?: string;
};

type GoogleReviewsDashboardFilters = {
  datasetId?: string;
  businessName?: string;
  businessNames?: string[];
};

type GoogleReviewsScrapeInput = {
  query?: string;
  queries?: string[];
  maxReviews?: number;
  sentiment?: boolean;
  batchSize?: number;
};

type GoogleReviewsAnalyzeInput = {
  datasetId?: string;
  businessName?: string;
  reviews?: Array<{
    author: string;
    rating: number;
    text: string;
  }>;
  model?: {
    modelId?: string;
    temperature?: number;
  };
};

type GoogleReviewsInsightsInput = {
  analyzedResult: {
    scores: GoogleReviewsAnalyzeResult['scores'];
    summary?: string;
  };
  outputStyle?: 'brief' | 'standard' | 'detailed';
};

type GoogleReviewsDeleteBusinessDataInput = {
  businessName?: string;
  datasetId?: string;
};

class GoogleReviewsService {
  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('token');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  private buildQueryParams(params: Record<string, string | number | string[] | undefined>): string {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          const serialized = value
            .map((item) => (typeof item === 'string' ? item.trim() : String(item)))
            .filter((item) => item.length > 0)
            .join(',');
          if (serialized) {
            searchParams.set(key, serialized);
          }
          return;
        }
        searchParams.set(key, String(value));
      }
    });
    const query = searchParams.toString();
    return query ? `?${query}` : '';
  }

  private async parseResponse<TData>(response: Response): Promise<TData> {
    const payload = await response.json();
    if (!response.ok || payload.success === false) {
      throw new Error(payload.error || payload.message || `Request failed (${response.status})`);
    }
    return payload as TData;
  }

  async getRunsHistory(filters: GoogleReviewsRunsFilters = {}): Promise<GoogleReviewsListResponse<GoogleReviewsRunHistoryItem>> {
    const query = this.buildQueryParams({
      page: filters.page ?? 1,
      pageSize: filters.pageSize ?? 10,
      runType: filters.runType,
      status: filters.status,
      businessName: filters.businessName,
      datasetId: filters.datasetId
    });
    const response = await fetch(`${API_BASE_URL}/google-reviews/runs${query}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    const payload = await this.parseResponse<ApiSuccessResponse<GoogleReviewsRunHistoryItem[]>>(response);
    return {
      data: payload.data || [],
      meta: payload.meta || { page: 1, pageSize: 10, total: 0, totalPages: 1 }
    };
  }

  async getReviews(filters: GoogleReviewsReviewFilters = {}): Promise<GoogleReviewsListResponse<GoogleReviewListItem>> {
    const query = this.buildQueryParams({
      page: filters.page ?? 1,
      pageSize: filters.pageSize ?? 25,
      datasetId: filters.datasetId,
      businessName: filters.businessName,
      source: filters.source,
      search: filters.search,
      minRating: filters.minRating,
      maxRating: filters.maxRating,
      publishedFrom: filters.publishedFrom,
      publishedTo: filters.publishedTo
    });
    const response = await fetch(`${API_BASE_URL}/google-reviews/reviews${query}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    const payload = await this.parseResponse<ApiSuccessResponse<GoogleReviewListItem[]>>(response);
    return {
      data: payload.data || [],
      meta: payload.meta || { page: 1, pageSize: 25, total: 0, totalPages: 1 }
    };
  }

  async getDashboardData(filters: GoogleReviewsDashboardFilters = {}): Promise<GoogleReviewsDashboardData> {
    const query = this.buildQueryParams({
      datasetId: filters.datasetId,
      businessName: filters.businessName,
      businessNames: filters.businessNames
    });
    const response = await fetch(`${API_BASE_URL}/google-reviews/dashboard${query}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    const payload = await this.parseResponse<ApiSuccessResponse<GoogleReviewsDashboardData>>(response);
    return payload.data;
  }

  async scrape(input: GoogleReviewsScrapeInput): Promise<GoogleReviewsScrapeActionResult> {
    const response = await fetch(`${API_BASE_URL}/google-reviews/actions/scrape`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ input })
    });
    const payload = await this.parseResponse<ApiSuccessResponse<GoogleReviewsScrapeActionResult>>(response);
    return payload.data;
  }

  async analyze(input: GoogleReviewsAnalyzeInput): Promise<GoogleReviewsAnalyzeResult> {
    const response = await fetch(`${API_BASE_URL}/google-reviews/actions/analyze`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ input })
    });
    const payload = await this.parseResponse<ApiSuccessResponse<GoogleReviewsAnalyzeResult>>(response);
    return payload.data;
  }

  async getInsights(input: GoogleReviewsInsightsInput): Promise<GoogleReviewsInsightsResult> {
    const response = await fetch(`${API_BASE_URL}/google-reviews/actions/insights`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ input })
    });
    const payload = await this.parseResponse<ApiSuccessResponse<GoogleReviewsInsightsResult>>(response);
    return payload.data;
  }

  async deleteBusinessData(input: GoogleReviewsDeleteBusinessDataInput): Promise<Record<string, number>> {
    const response = await fetch(`${API_BASE_URL}/google-reviews/business-data`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(input)
    });
    const payload = await this.parseResponse<ApiSuccessResponse<Record<string, number>>>(response);
    return payload.data || {};
  }
}

const googleReviewsService = new GoogleReviewsService();
export default googleReviewsService;
